export const API = {
  // ============================================================
  // AUTH CONTROLLER  (/api/auth)
  // ============================================================
  auth: {
    signin: '/api/auth/SignIn', // POST
    forgotPassword: '/api/auth/ForgotPassword', // POST
    resetPassword: '/api/auth/ResetPassword', // POST
    updatePassword: '/api/auth/UpdatePassword', // POST
    resendInvitation: (guid: string) => `/api/auth/resend-invitation/${guid}`, // POST
    resetPasswordMail: (guid: string) => `/api/auth/reset-password/${guid}`, // POST
    isValid: '/api/auth/is-valid', // GET
  },

  // ============================================================
  // USERS CONTROLLER  (/api/users)
  // ============================================================
  users: {
    base: '/api/users', // GET

    getAll: '/api/users', // GET

    getByGuid: (guid: string) => `/api/users/${guid}`, // GET
    getWithCustomer: (guid: string) => `/api/users/${guid}/with-customer`, // GET
    getAllWithCustomers: '/api/users/with-customers', // GET

    getUsersWithoutRelation: '/api/users/GetOnlyUsersWithoutRelation', // GET

    getSingleCustomerUsers: (customerGuid: string) =>
      `/api/users/GetSingleCustomerWithUsers/${customerGuid}`, // GET

    createCustomerAdmin: '/api/users', // POST
    createInternalAdmin: '/api/users/admin', // POST

    withCustomer: (guid: string) => `/api/users/${guid}/with-customer`, // GET

    updateUserRole: (guid: string) => `/api/users/${guid}/role`, // PUT

    update: (guid: string) => `/api/users/${guid}`, // PUT
    delete: (guid: string) => `/api/users/${guid}`, // DELETE
    canDelete: (guid: string) => `/api/users/${guid}/can-delete`, // GET
    adminDelete: (id: string) => `/api/users/admin/${id}`, // DELETE
  },

  // ============================================================
  // CUSTOMERS CONTROLLER  (/api/customers)
  // ============================================================
  customers: {
    base: '/api/customers', // GET
    getAll: '/api/customers', // GET

    getByGuid: (guid: string) => `/api/customers/${guid}`, // GET
    withAddress: (guid: string) => `/api/customers/${guid}/with-address`, // GET
    withLicenses: (guid: string) => `/api/customers/${guid}/with-licenses`, // GET
    withDetails: (guid: string) => `/api/customers/${guid}/with-details`, // GET
    visibleProducts: (guid: string) =>
      `/api/customers/${guid}/visible-products`, // GET
    getWithCustomer: (guid: string) => `/api/customers/${guid}/with-customer`, // GET

    addUser: (customerGuid: string) =>
      `/api/customers/${customerGuid}/add-user`, // PUT
    removeUser: (userGuid: string) => `/api/customers/remove-user/${userGuid}`, // PUT

    update: (guid: string) => `/api/customers/${guid}`, // PUT
    delete: (guid: string) => `/api/customers/${guid}`, // DELETE

    enable: (guid: string) => `/api/customers/${guid}/enable`, // PUT
    whiteLabel: (guid: string) => `/api/customers/${guid}/whitelabel`, // PUT
  },

  // ============================================================
  // ADDRESSES CONTROLLER ( /api/customers/{customerGuid}/addresses )
  // ============================================================
  addresses: {
    add: (customerGuid: string) => `/api/customers/${customerGuid}/addresses`, // POST

    delete: (customerGuid: string, addressGuid: string) =>
      `/api/customers/${customerGuid}/addresses/${addressGuid}`, // DELETE

    update: (customerGuid: string, addressGuid: string) =>
      `/api/customers/${customerGuid}/addresses/${addressGuid}`, // PUT
  },

  // ============================================================
  // PERMISSIONS CONTROLLER (/api/permissions)
  // ============================================================
  permissions: {
    getAll: '/api/permissions', // GET
  },

  // ============================================================
  // ROLES CONTROLLER (/api/roles)
  // ============================================================
  roles: {
    getAll: '/api/roles', // GET
    getAllRolePermissions: '/api/roles/permissions', // GET

    delete: (roleName: string) => `/api/roles/${roleName}`, // DELETE

    getRolePermissions: (roleName: string) =>
      `/api/roles/${roleName}/permissions`, // GET

    addPermission: (roleName: string, perm: string) =>
      `/api/roles/${roleName}/permissions/${perm}`, // POST ADD

    removePermission: (roleName: string, perm: string) =>
      `/api/roles/${roleName}/permissions/${perm}`, // POST REMOVE
  },

  // ============================================================
  // LICENCE CONTROLLER (/api/licence)
  // ============================================================
  licence: {
    base: '/api/licence', // GET

    getAll: '/api/licence', // GET
    getByGuid: (guid: string) => `/api/licence/${guid}`, // GET

    delete: '/api/licence', // DELETE
    disable: '/api/licence/disable', // DELETE
    enable: '/api/licence', // PUT

    // -------------------------------
    // General Licence Endpoints
    // -------------------------------

    softwareTypes: '/api/licence/software_types', // GET
    licenseTypes: (softwareType: string) =>
      `/api/licence/licenses_types/${softwareType}`, // GET

    upload: (softwareType: string, customerGuid: string, licenceType: string) =>
      `/api/licence/upload/${softwareType}/${customerGuid}?licence_type=${licenceType}`, // POST

    updateSupport: '/api/licence/support', // PUT

    // -------------------------------
    // SAD Creation Endpoints
    // -------------------------------
    getSADOverview: '/api/licence/sad/overview', // GET
    sadCreate: '/api/licence/sad/create', // POST
    sadCreateAssign: (customerGuid: string) =>
      `/api/licence/sad/create-assign/${customerGuid}`, // POST
    sadUpdate: (guid: string) => `/api/licence/sad/${guid}`, // PUT
    sadAssign: (licenceGuid: string, customerGuid: string) =>
      `/api/licence/sad/assign/${licenceGuid}/${customerGuid}`, // PUT
    sadReassign: (licenceGuid: string, customerGuid: string) =>
      `/api/licence/sad/reassign/${licenceGuid}/${customerGuid}`, // PUT
    sadRenew: (licenceGuid: string) => `/api/licence/sad/renew/${licenceGuid}`, // PUT
  },

  // ============================================================
  // SOFTWARE CONTROLLER (/api/software)
  // ============================================================
  software: {
    base: '/api/software', // GET, POST

    getAll: '/api/software', // GET
    getByGuid: (guid: string) => `/api/software/${guid}`, // GET

    create: '/api/software', // POST
    update: (guid: string) => `/api/software/${guid}`, // PUT
    delete: (guid: string) => `/api/software/${guid}`, // DELETE

    forCustomer: (customerGuid: string) =>
      `/api/software/customer/${customerGuid}`, // GET
    setAccess: '/api/software/access', // PUT
    setGlobalAccess: (guid: string) => `/api/software/${guid}/global-access`, // PUT
  },

  // ============================================================
  // DOWNLOAD CONTROLLER (/api/download)
  // ============================================================
  download: {
    file: (filename: string) => `/api/download/file?filename=${filename}`, // GET
    grouped: '/api/download/grouped', // GET
    zip: (folder: string) => `/api/download/zip?folder=${folder}`, // GET
  },
  // ============================================================
  // REPORT CONTROLLER (/api/report)
  // ============================================================
  report: {
    downloads: (query: string) =>
      `/api/report/downloads${query ? `?${query}` : ''}`, // GET
    downloadsStats: (query: string) =>
      `/api/report/downloads/stats${query ? `?${query}` : ''}`, // GET
    downloadsProductStats: (query: string) =>
      `/api/report/downloads/product-stats${query ? `?${query}` : ''}`, // GET
    downloadsFacets: () => `/api/report/downloads/facets`, // GET
    downloadsCsv: (query: string) =>
      `/api/report/downloads/csv${query ? `?${query}` : ''}`, // GET
    changes: (query: string) =>
      `/api/report/changes${query ? `?${query}` : ''}`, // GET
    changesCsv: (query: string) =>
      `/api/report/changes/csv${query ? `?${query}` : ''}`, // GET
  },
  // ============================================================
  // DOWNLOADS AREA CONTROLLER (/api/downloads)
  // Serves the download area (listing/file/zip) from the configured
  // storage backend; every access is authenticated (JWT) and logged.
  // ============================================================
  downloadsArea: {
    listing: '/api/downloads/listing', // GET
    file: (file: string) =>
      `/api/downloads/file?file=${encodeURIComponent(file)}`, // GET
    zip: (dir: string) => `/api/downloads/zip?dir=${encodeURIComponent(dir)}`, // GET
  },
  // ============================================================
  // EXTERNAL SETTINGS CONTROLLER (/api/admin/external-settings)
  // ============================================================
  externalSettings: {
    storage: '/api/admin/external-settings/storage', // GET + PUT
    storageTest: '/api/admin/external-settings/storage/test', // POST
    storageType: '/api/admin/external-settings/storage/type', // PUT
    sftp: '/api/admin/external-settings/storage/sftp', // PUT
    sftpTest: '/api/admin/external-settings/storage/sftp/test', // POST
    smtp: '/api/admin/external-settings/smtp', // GET + PUT
    smtpTest: '/api/admin/external-settings/smtp/test', // POST
    smtpTestMail: '/api/admin/external-settings/smtp/test-mail', // POST
    deepl: '/api/admin/external-settings/deepl', // GET + PUT
    deeplTest: '/api/admin/external-settings/deepl/test', // POST
    flowster: '/api/admin/external-settings/flowster', // GET + PUT
    flowsterTest: '/api/admin/external-settings/flowster/test', // POST
  },

  // ============================================================
  // MAIL CONTROLLER (/api/admin/mail)
  // ============================================================
  mail: {
    sendMode: (guid: string) => `/api/admin/mail/templates/${guid}/send-mode`, // PATCH

    requests: '/api/admin/mail/requests', // GET
    requestByGuid: (guid: string) => `/api/admin/mail/requests/${guid}`, // GET
    requestLogs: (guid: string) => `/api/admin/mail/requests/${guid}/logs`, // GET
    recipients: (guid: string) => `/api/admin/mail/requests/${guid}/recipients`, // GET
    sendRequest: (guid: string) => `/api/admin/mail/${guid}/send`, // POST
    updateRequest: (guid: string) => `/api/admin/mail/requests/${guid}`, // POST

    templates: '/api/admin/mail/templates', // GET
    templateByGuid: (guid: string) => `/api/admin/mail/templates/${guid}`, // GET
    updateTemplate: (guid: string) => `/api/admin/mail/templates/${guid}`, // PUT
    templateVariablesByGuid: (guid: string) =>
      `/api/admin/mail/templates/${guid}/variables`, //GET

    uploadImage: '/api/admin/mail/uploads/images', // POST
    cleanupImages: '/api/admin/mail/uploads/cleanupImages', // POST
  },
};
